import React, { useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, GridOptions, ValueFormatterParams } from 'ag-grid-community';

import DataTable from 'components/DataTable';
import useIsDataTable from 'components/DataTable/useIsDataTable';
import StyleOverrides from 'components/Grid/StyleOverrides';
import { InsightsChartSpec } from 'services/streamInsightsQuery';

import { formatChartValue, INSIGHTS_CHART_HEIGHT } from './buildChartOptions';

// Renders an agent chart's underlying numbers as a table — the grid half of the
// chart/grid toggle. The spec already carries the data in tabular form
// (`categories` rows × `series` columns), so nothing is re-fetched.
//
// We drive AgGridReact directly under the FleetOps grid theme rather than the
// components/Grid wrapper: that wrapper is a GraphQL data-fetching container and
// its inner Grid unconditionally mounts board/period/goal slideouts, none of
// which exist in the chat provider tree. Here we only reuse the grid engine and
// StyleOverrides (the app's grid look), fed hand-built colDefs + rowData.

type GridRow = Record<string, string | number | null>;

// Pure spec → { columnDefs, rowData }. Split out from the component so the
// mapping + formatting is unit-testable without mounting AG-Grid in jsdom.
export const buildInsightsGridConfig = (
  spec: InsightsChartSpec,
): { columnDefs: ColDef[]; rowData: GridRow[] } => {
  const valueFormatter = (params: ValueFormatterParams): string =>
    params.value == null
      ? '—'
      : formatChartValue(Number(params.value), spec.formatting);

  const columnDefs: ColDef[] = [
    {
      headerName: spec.categoryLabel ?? '',
      field: 'category',
      pinned: 'left',
    },
    ...spec.series.map(
      (s, index): ColDef => ({
        headerName: s.name,
        field: `s${index}`,
        type: 'numericColumn',
        valueFormatter,
      }),
    ),
  ];

  const rowData: GridRow[] = spec.categories.map((category, rowIndex) => {
    const row: GridRow = { category };
    spec.series.forEach((s, seriesIndex) => {
      row[`s${seriesIndex}`] = s.data[rowIndex] ?? null;
    });
    return row;
  });

  return { columnDefs, rowData };
};

const GRID_OPTIONS: GridOptions = {
  defaultColDef: {
    resizable: true,
    sortable: true,
    suppressMovable: true,
  },
  suppressCellFocus: true,
};

const InsightsMessageGrid = ({ spec }: { spec: InsightsChartSpec }) => {
  const isDataTable = useIsDataTable('insightsGrid');
  const { columnDefs, rowData } = useMemo(
    () => buildInsightsGridConfig(spec),
    [spec],
  );

  if (isDataTable) {
    return (
      <div
        style={{ height: INSIGHTS_CHART_HEIGHT, width: '100%' }}
        data-testid="insights-message-grid"
      >
        <DataTable
          columnDefs={columnDefs}
          defaultColDef={GRID_OPTIONS.defaultColDef}
          rows={rowData}
          // The table fills the chart's width, the category column as wide
          // as its labels (AG gives every column its default 200px)
          sizing="fitWidth"
          isFirstColumnFitted
        />
      </div>
    );
  }

  return (
    <StyleOverrides
      style={{ height: INSIGHTS_CHART_HEIGHT, width: '100%' }}
      data-testid="insights-message-grid"
    >
      <div
        className="ag-theme-balham ag-theme-fleetops"
        style={{ height: '100%', width: '100%' }}
      >
        <AgGridReact
          columnDefs={columnDefs}
          rowData={rowData}
          gridOptions={GRID_OPTIONS}
        />
      </div>
    </StyleOverrides>
  );
};

export default InsightsMessageGrid;
